import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Check, CheckCircle2, Loader2, Pencil, Sparkles, Tag } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  primaryText,
  secondaryText,
  selectableName,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../../styling/stylingHelper';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';
import {
  getCategoryDisplayName,
  getCategoryEmoji,
  getMainCategoryDisplayName,
} from '../../orderhistory/categoryUtils';
import { MAIN_CATEGORIES, FOOD_CATEGORIES, DRINKS_CATEGORIES, NON_FOOD_CATEGORIES } from '../../orderhistory/utils';
import { formatPlatformName } from '../../../services/helperService';
import {
  checkCategoryChunk,
  applyCategoryFixes,
  CategoryCheckRow,
  CategoryCheckFinding,
  CategoryFix,
  CategoryApplySummary,
  CATEGORY_CHECK_CHUNK_SIZE,
} from '../../../services/orderhistory/categoryCheckService';

/** One products-page row, ready to send. `subCategories` rides along for the apply step only. */
export interface CategoryCheckTarget extends CategoryCheckRow {
  subCategories?: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** For AI-cost attribution only; null in the All Restaurants view. */
  restaurantId: string | null;
  /** The rows currently on screen, already mapped by the caller. */
  targets: CategoryCheckTarget[];
  /** Fired after an apply that changed something, so the page can reload its order history. */
  onApplied: () => void;
}

/**
 * 'intro'   states what will be checked (the pass costs AI budget, so it is one deliberate click)
 * 'running' one request per chunk, with a progress bar and a stop button
 * 'review'  the findings, each pre-accepted and each editable inline
 * 'done'    what actually landed, including per-row failures
 */
type Step = 'intro' | 'running' | 'review' | 'done';

/** Fixes per apply request — the route rejects more than this in one go. */
const APPLY_CHUNK_SIZE = 500;

/** The user's own choice for a row, replacing whatever the reviewer proposed. */
interface RowEdit {
  mainCategory: string;
  category: string;
}

const chipClass = 'rounded-lg border px-2 py-1 text-[12px] font-medium transition-colors';
const chipActive = 'border-transparent bg-[#0E8965] text-white dark:text-white';
const chipIdle = cn('text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark', borderPrimary);

function categoriesFor(mainCategory: string): readonly string[] {
  if (mainCategory === 'FOOD') return FOOD_CATEGORIES;
  if (mainCategory === 'DRINKS') return DRINKS_CATEGORIES;
  if (mainCategory === 'NON_FOOD') return NON_FOOD_CATEGORIES;
  return [];
}

/**
 * The picker's options for one main category, with `keep` folded in when the shared list does not
 * carry it. `NON_FOOD_CATEGORIES` (frontend) is missing EMBALLAGE, which the server enum has and the
 * reviewer can legitimately propose — without this the proposal would be un-selectable, and
 * re-opening the row would silently swap it for something else.
 */
function pickerOptions(mainCategory: string, keep?: string): string[] {
  const options = [...categoriesFor(mainCategory)];
  if (keep && !options.includes(keep)) options.push(keep);
  return options;
}

/** A category badge — same emoji + display name pairing the products table uses. */
function CategoryBadge({ mainCategory, category }: { mainCategory?: string; category?: string }) {
  const { t } = useTranslation();
  if (!mainCategory || !category) {
    return <span className={cn('text-[12px]', tertiaryText)}>{t('products.categoryCheck.noCategory')}</span>;
  }
  return (
    <span className={cn('text-[12px] inline-flex items-center gap-1', primaryText)}>
      <span>{getCategoryEmoji(category)}</span>
      <span className={semiBoldText}>{getCategoryDisplayName(category, mainCategory)}</span>
      <span className={tertiaryText}>· {getMainCategoryDisplayName(mainCategory)}</span>
    </span>
  );
}

/**
 * "Check categories": a second AI model audits the categories stored on the products currently on
 * screen and proposes fixes. Nothing is written until the user hits Apply — and then every accepted
 * row goes through the normal category update, which spans the account's whole order history rather
 * than the period being viewed.
 *
 * Mirrors the inventory "Link products" flow (analyse → review → commit) with one difference that
 * matters: the analyse step is CHUNKED across requests, because a full catalog is minutes of model
 * time and the user needs progress and a way out.
 */
export default function CategoryCheckModal({ isOpen, onClose, restaurantId, targets, onApplied }: Props) {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('intro');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [findings, setFindings] = useState<CategoryCheckFinding[]>([]);
  const [checkedCount, setCheckedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [failedBatches, setFailedBatches] = useState(0);
  const [wasStopped, setWasStopped] = useState(false);

  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [editingRow, setEditingRow] = useState<string | null>(null);

  const [summary, setSummary] = useState<CategoryApplySummary | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const chunks = useMemo(() => {
    const out: CategoryCheckTarget[][] = [];
    for (let i = 0; i < targets.length; i += CATEGORY_CHECK_CHUNK_SIZE) {
      out.push(targets.slice(i, i + CATEGORY_CHECK_CHUNK_SIZE));
    }
    return out;
  }, [targets]);

  const targetByRowKey = useMemo(() => {
    const map = new Map<string, CategoryCheckTarget>();
    for (const target of targets) map.set(target.rowKey, target);
    return map;
  }, [targets]);

  // Fresh state on every open — a previous run's findings are stale the moment a category changes.
  useEffect(() => {
    if (!isOpen) return;
    setStep('intro');
    setBusy(false);
    setError(null);
    setFindings([]);
    setCheckedCount(0);
    setSkippedCount(0);
    setFailedBatches(0);
    setWasStopped(false);
    setAccepted({});
    setEdits({});
    setEditingRow(null);
    setSummary(null);
  }, [isOpen]);

  // An in-flight run must not outlive the modal: its chunks would keep spending AI budget with
  // nowhere to land.
  useEffect(() => () => abortRef.current?.abort(), []);

  const runCheck = async () => {
    const controller = new AbortController();
    abortRef.current = controller;

    setStep('running');
    setBusy(true);
    setError(null);
    setWasStopped(false);

    const collected: CategoryCheckFinding[] = [];
    let checked = 0;
    let skipped = 0;
    let failed = 0;

    try {
      for (const chunk of chunks) {
        const res = await checkCategoryChunk(restaurantId, chunk, controller.signal);
        collected.push(...res.findings);
        checked += res.checked;
        skipped += res.skipped;
        failed += res.failedBatches;

        // Published per chunk so the progress bar and the running findings count are live.
        setFindings([...collected]);
        setCheckedCount(checked);
        setSkippedCount(skipped);
        setFailedBatches(failed);
      }
      setAccepted(Object.fromEntries(collected.map(f => [f.rowKey, true])));
      setStep('review');
    } catch (err) {
      if (controller.signal.aborted) {
        // Stopping is not failing: keep what already came back and review it.
        setWasStopped(true);
        setAccepted(Object.fromEntries(collected.map(f => [f.rowKey, true])));
        setStep(collected.length > 0 ? 'review' : 'intro');
        return;
      }
      console.error('Category check failed:', err);
      setError(t('products.categoryCheck.errorCheck'));
      setStep('intro');
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const chosenFor = (finding: CategoryCheckFinding): RowEdit =>
    edits[finding.rowKey] ?? {
      mainCategory: finding.suggestedMainCategory,
      category: finding.suggestedCategory,
    };

  const acceptedFindings = useMemo(
    () => findings.filter(f => accepted[f.rowKey]),
    [findings, accepted]
  );

  const runApply = async () => {
    if (acceptedFindings.length === 0) return;
    setBusy(true);
    setError(null);

    const fixes: CategoryFix[] = [];
    for (const finding of acceptedFindings) {
      const target = targetByRowKey.get(finding.rowKey);
      if (!target) continue;
      const chosen = chosenFor(finding);
      fixes.push({
        rowKey: finding.rowKey,
        restaurantPlatformId: target.restaurantPlatformId,
        articleId: target.articleId,
        productName: target.productName,
        mainCategory: chosen.mainCategory,
        category: chosen.category,
        // The row's CURRENT subcategories: the server keeps only the ones legal under the new
        // category, exactly as the manual category edit does.
        subCategories: target.subCategories ?? [],
      });
    }

    try {
      // Chunked because the route caps a single apply — a catalog with hundreds of uncategorised
      // products can easily exceed it.
      const combined: CategoryApplySummary = { applied: 0, failed: 0, ordersUpdated: 0, outcomes: [] };
      for (let i = 0; i < fixes.length; i += APPLY_CHUNK_SIZE) {
        const res = await applyCategoryFixes(fixes.slice(i, i + APPLY_CHUNK_SIZE));
        combined.applied += res.applied;
        combined.failed += res.failed;
        combined.ordersUpdated += res.ordersUpdated;
        combined.outcomes.push(...res.outcomes);
      }
      setSummary(combined);
      setStep('done');
      if (combined.applied > 0) onApplied();
    } catch (err) {
      console.error('Category fix apply failed:', err);
      setError(t('products.categoryCheck.errorApply'));
    } finally {
      setBusy(false);
    }
  };

  const wrongFindings = findings.filter(f => f.verdict === 'wrong');
  const missingFindings = findings.filter(f => f.verdict === 'missing');

  // Skipped rows (money lines, emballage) are DONE, not pending — the bar and the label must agree.
  const processedCount = checkedCount + skippedCount;
  const progressPct = Math.min(100, Math.round((processedCount / Math.max(1, targets.length)) * 100));

  const renderRow = (finding: CategoryCheckFinding) => {
    const isAccepted = !!accepted[finding.rowKey];
    const chosen = chosenFor(finding);
    const isEditing = editingRow === finding.rowKey;
    const wasEdited = !!edits[finding.rowKey];

    return (
      <div key={finding.rowKey} className={cn('p-3 rounded-xl border', borderPrimary, !isAccepted && 'opacity-60')}>
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isAccepted}
            onChange={() => setAccepted(prev => ({ ...prev, [finding.rowKey]: !prev[finding.rowKey] }))}
            aria-label={t('products.categoryCheck.acceptRow') as string}
            className="mt-0.5 shrink-0 rounded border-border dark:border-border-dark"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className={cn(smallTextSize, semiBoldText, primaryText, selectableName)}>{finding.productName}</p>
              {/* An invoice-only row's platform is a bucket, so show the real vendor when there is one. */}
              <span className={cn('text-[11px] shrink-0', tertiaryText)}>
                {targetByRowKey.get(finding.rowKey)?.supplierName || formatPlatformName(finding.platform)}
              </span>
            </div>

            {/* current → proposed */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <CategoryBadge mainCategory={finding.currentMainCategory} category={finding.currentCategory} />
              <ArrowRight className={cn('w-3.5 h-3.5 shrink-0', tertiaryText)} />
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-green-50 dark:bg-green-900/20">
                <CategoryBadge mainCategory={chosen.mainCategory} category={chosen.category} />
              </span>
              <button
                type="button"
                onClick={() => setEditingRow(isEditing ? null : finding.rowKey)}
                className={cn('text-[11px] px-2 py-0.5 rounded-lg border flex items-center gap-1', borderPrimary, isEditing ? 'bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/10 text-darkGreenColor dark:text-darkGreenColor-dark' : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
              >
                <Pencil className="w-3 h-3" /> {t('products.categoryCheck.edit')}
              </button>
            </div>

            {(finding.reason || finding.verdict === 'wrong' || wasEdited) && (
              <p className={cn('mt-1 text-[11px]', tertiaryText)}>
                {finding.reason}
                {finding.verdict === 'wrong' && (
                  <span className="ml-1">
                    · {t('products.categoryCheck.confidence', { pct: Math.round(finding.confidence * 100) })}
                  </span>
                )}
                {wasEdited && <span className="ml-1 text-amber-600 dark:text-amber-400">· {t('products.categoryCheck.edited')}</span>}
              </p>
            )}

            {/* Inline picker: main category, then the categories legal under it. */}
            {isEditing && (
              <div className={cn('mt-2 p-2 rounded-xl border', borderPrimary, 'bg-gray-50/60 dark:bg-zinc-800/40')}>
                <div className={cn('mb-1.5 text-[11px]', tertiaryText)}>{t('products.categoryCheck.mainCategory')}</div>
                <div className="flex flex-wrap gap-1.5">
                  {MAIN_CATEGORIES.map(main => (
                    <button
                      key={main.value}
                      type="button"
                      onClick={() =>
                        setEdits(prev => ({
                          ...prev,
                          // Switching side invalidates the category, so land on its first option.
                          [finding.rowKey]: { mainCategory: main.value, category: pickerOptions(main.value)[0] },
                        }))
                      }
                      className={cn(chipClass, chosen.mainCategory === main.value ? chipActive : chipIdle)}
                    >
                      {getMainCategoryDisplayName(main.value)}
                    </button>
                  ))}
                </div>

                <div className={cn('mt-2 mb-1.5 text-[11px]', tertiaryText)}>{t('products.categoryCheck.category')}</div>
                <div className="flex flex-wrap gap-1.5">
                  {pickerOptions(chosen.mainCategory, chosen.category).map(category => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => {
                        setEdits(prev => ({ ...prev, [finding.rowKey]: { mainCategory: chosen.mainCategory, category } }));
                        setEditingRow(null);
                        // Editing a row is an implicit accept — nobody picks a category to skip it.
                        setAccepted(prev => ({ ...prev, [finding.rowKey]: true }));
                      }}
                      className={cn(chipClass, chosen.category === category ? chipActive : chipIdle)}
                    >
                      {getCategoryEmoji(category)} {getCategoryDisplayName(category, chosen.mainCategory)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getFooter = () => {
    if (step === 'intro') {
      return (
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={runCheck}
            disabled={busy || targets.length === 0}
            className={slideOverPrimaryClass}
          >
            {t('products.categoryCheck.start', { n: targets.length })}
          </button>
        </>
      );
    }
    if (step === 'running') {
      return (
        <button type="button" onClick={() => abortRef.current?.abort()} className={slideOverCancelClass}>
          {t('products.categoryCheck.stop')}
        </button>
      );
    }
    if (step === 'review') {
      return (
        <>
          <button type="button" onClick={() => setStep('intro')} disabled={busy} className={slideOverCancelClass}>
            {t('common.back')}
          </button>
          <button
            type="button"
            onClick={runApply}
            disabled={busy || acceptedFindings.length === 0}
            className={slideOverPrimaryClass}
          >
            {busy
              ? t('products.categoryCheck.applying')
              : t('products.categoryCheck.apply', { n: acceptedFindings.length })}
          </button>
        </>
      );
    }
    return (
      <button type="button" onClick={onClose} className={slideOverPrimaryClass}>
        {t('common.close')}
      </button>
    );
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('products.categoryCheck.title')}
      description={t('products.categoryCheck.description')}
      footer={getFooter()}
      closeLabel={t('common.close') as string}
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {step === 'intro' && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <p className={cn(smallTextSize, primaryText, semiBoldText, 'flex items-center gap-2')}>
            <Sparkles className="w-4 h-4 text-darkGreenColor dark:text-darkGreenColor-dark shrink-0" />
            {t('products.categoryCheck.introCount', { n: targets.length })}
          </p>
          <p className={cn('mt-2 text-[13px] leading-5', secondaryText)}>{t('products.categoryCheck.introBody')}</p>
          <p className={cn('mt-2 text-[13px] leading-5', tertiaryText)}>{t('products.categoryCheck.introScope')}</p>
        </div>
      )}

      {step === 'running' && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={cn(smallTextSize, semiBoldText, primaryText, 'flex items-center gap-2')}>
              <Loader2 className="w-4 h-4 animate-spin text-darkGreenColor dark:text-darkGreenColor-dark" />
              {t('products.categoryCheck.progress', { done: processedCount, total: targets.length })}
            </span>
            <span className={cn('text-[11px]', tertiaryText)}>{progressPct}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className={cn('mt-3 text-[13px]', secondaryText)}>
            {t('products.categoryCheck.foundSoFar', { n: findings.length })}
          </p>
        </div>
      )}

      {step === 'review' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { label: t('products.categoryCheck.summaryChecked'), value: checkedCount },
              { label: t('products.categoryCheck.summaryWrong'), value: wrongFindings.length },
              { label: t('products.categoryCheck.summaryMissing'), value: missingFindings.length },
            ].map((chip, i) => (
              <span key={i} className={cn(smallTextSize, 'px-3 py-1.5 rounded-lg border', borderPrimary, secondaryText)}>
                {chip.label}: <span className={cn(semiBoldText, primaryText)}>{chip.value}</span>
              </span>
            ))}
          </div>

          {wasStopped && (
            <p className={cn(smallTextSize, tertiaryText, 'mb-3 flex items-start gap-2')}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px text-amber-600 dark:text-amber-400" />
              {t('products.categoryCheck.stoppedEarly', { done: processedCount, total: targets.length })}
            </p>
          )}

          {failedBatches > 0 && (
            <p className={cn(smallTextSize, tertiaryText, 'mb-3 flex items-start gap-2')}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px text-amber-600 dark:text-amber-400" />
              {t('products.categoryCheck.someBatchesFailed')}
            </p>
          )}

          {findings.length === 0 ? (
            <div className={cn('p-4 rounded-xl border', borderPrimary)}>
              <p className={cn(smallTextSize, primaryText, semiBoldText, 'flex items-center gap-2')}>
                <CheckCircle2 className="w-4 h-4 text-darkGreenColor dark:text-darkGreenColor-dark shrink-0" />
                {t('products.categoryCheck.allGood')}
              </p>
              <p className={cn('mt-2 text-[13px] leading-5', secondaryText)}>{t('products.categoryCheck.allGoodBody')}</p>
            </div>
          ) : (
            <div className="space-y-5">
              {wrongFindings.length > 0 && (
                <div>
                  <p className={cn(smallTextSize, semiBoldText, primaryText, 'mb-2 flex items-center gap-2')}>
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    {t('products.categoryCheck.groupWrong', { n: wrongFindings.length })}
                  </p>
                  <div className="space-y-2">{wrongFindings.map(renderRow)}</div>
                </div>
              )}

              {missingFindings.length > 0 && (
                <div>
                  <p className={cn(smallTextSize, semiBoldText, primaryText, 'mb-2 flex items-center gap-2')}>
                    <Tag className={cn('w-4 h-4 shrink-0', tertiaryText)} />
                    {t('products.categoryCheck.groupMissing', { n: missingFindings.length })}
                  </p>
                  <div className="space-y-2">{missingFindings.map(renderRow)}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 'done' && summary && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <p className={cn(smallTextSize, semiBoldText, primaryText, 'flex items-center gap-2')}>
            <Check className="w-4 h-4 text-darkGreenColor dark:text-darkGreenColor-dark shrink-0" />
            {t('products.categoryCheck.doneApplied', { n: summary.applied, orders: summary.ordersUpdated })}
          </p>
          {summary.failed > 0 && (
            <>
              <p className={cn('mt-3 text-[12px]', secondaryText)}>
                {t('products.categoryCheck.doneFailed', { n: summary.failed })}
              </p>
              <div className="mt-1 space-y-1">
                {summary.outcomes.filter(o => o.error).map(o => (
                  <p key={o.rowKey} className={cn('text-[11px]', tertiaryText)}>
                    {o.productName}: {o.error}
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </InventorySlideOver>
  );
}
